import { useState, useEffect } from 'react';
import DateRangePicker from './DateRangePicker';
import { supabase } from '../lib/supabase';
import { fmt$0, fmtD } from '../utils/formatters';
import { getRange, fmtDateShort } from '../utils/dateRange';
import { parseLocalDate } from '../utils/dates';
import VendorDropdown from './VendorDropdown';

const RANGE_PRESETS=[{id:'week',label:'Week'},{id:'this-month',label:'This Month'},{id:'last-month',label:'Last Month'},{id:'last-3-months',label:'Last 3 Months'},{id:'ytd',label:'Year to Date'},{id:'custom',label:'Custom'}];

export default function SwappedInvoices({orgId,t,sb,sh,sd,mn,si,settings,salesDate,setInvoiceModalId,setDuplicateData,setPg}){
const[pairs,setPairs]=useState([]);
const[loading,setLoading]=useState(true);
const[error,setError]=useState('');
const[viewMode,setViewMode]=useState('ytd');
const[customStart,setCustomStart]=useState('');
const[customEnd,setCustomEnd]=useState('');
const[weekOffset,setWeekOffset]=useState(0);
const[vendorFilter,setVendorFilter]=useState([]);

async function loadPairs(){
  setLoading(true);
  const{data:old}=await supabase.from('invoices').select('id,vendor,invoice_number,invoice_date,grand_total,doc_type,superseded_by,created_at').eq('org_id',orgId).not('superseded_by','is',null).order('created_at',{ascending:false});
  if(!old?.length){setPairs([]);setLoading(false);return;}
  const newIds=[...new Set(old.map(o=>o.superseded_by))];
  const{data:newInvs}=await supabase.from('invoices').select('id,vendor,invoice_number,invoice_date,grand_total,doc_type,created_at').in('id',newIds);
  const newMap=new Map((newInvs||[]).map(n=>[n.id,n]));
  setPairs(old.map(o=>({old:o,new:newMap.get(o.superseded_by)||null})));
  setLoading(false);
}
useEffect(()=>{if(orgId)loadPairs();},[orgId]);

const fmtDate=d=>d?parseLocalDate(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'}):'—';

// Compute date range
let rangeStart,rangeEnd,rangeLabel;
if(viewMode==='week'){
  const d3=parseLocalDate(salesDate);const dow3=d3.getDay();const diff3=(dow3-(settings?.weekStart||0)+7)%7;
  const ws3=new Date(d3);ws3.setDate(d3.getDate()-diff3+weekOffset*7);
  const we3=new Date(ws3);we3.setDate(ws3.getDate()+6);
  rangeStart=ws3.toISOString().split("T")[0];rangeEnd=we3.toISOString().split("T")[0];
  rangeLabel=`Week of ${fmtD(rangeStart)} – ${fmtD(rangeEnd)}`;
}else{
  const r=getRange(viewMode,{start:customStart,end:customEnd});
  rangeStart=r.start;rangeEnd=r.end;rangeLabel=viewMode==='custom'?`${fmtDateShort(r.start)} – ${fmtDateShort(r.end)}`:r.label;
}

const dateFiltered=pairs.filter(p=>{const sd2=p.new?.created_at?.split('T')[0];return sd2&&sd2>=rangeStart&&sd2<=rangeEnd;});
const shown=vendorFilter.length===0?dateFiltered:dateFiltered.filter(p=>vendorFilter.includes(p.old.vendor));

return(<div>
<h1 style={{fontSize:22,fontWeight:700,margin:'0 0 6px'}}>Swapped Invoices</h1>
<p style={{color:t.tm,fontSize:13,margin:'0 0 20px'}}>{shown.length} swap{shown.length!==1?'s':''}{shown.length!==pairs.length?` of ${pairs.length} total`:''}</p>

{loading?<div style={{textAlign:'center',padding:40,color:t.td}}>Loading...</div>:
pairs.length===0?<div style={{textAlign:'center',padding:40,color:t.td,fontSize:12}}>No swapped invoices.</div>:

<>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:14,marginBottom:12}}>
{/* Date range controls */}
<div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap",alignItems:"center"}}>
{RANGE_PRESETS.map(p=><button key={p.id} onClick={()=>{setViewMode(p.id);setWeekOffset(0);}} style={{...sb,background:viewMode===p.id?t.bl:t.sf,color:viewMode===p.id?"#fff":t.tm,border:`1px solid ${viewMode===p.id?t.bl:t.bd}`,padding:"5px 12px",fontSize:10}}>{p.label}</button>)}
</div>
{viewMode==='custom'&&<DateRangePicker start={customStart} end={customEnd} onChange={(s,e)=>{setCustomStart(s);setCustomEnd(e);}} t={t} si={si}/>}

{/* Week nav */}
{viewMode==='week'&&<div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
<button onClick={()=>setWeekOffset(p=>p-1)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,padding:"4px 10px",fontSize:12}}>←</button>
<div style={{fontSize:12,color:t.tm,flex:1,textAlign:"center",fontWeight:600}}>{rangeLabel}</div>
<button onClick={()=>setWeekOffset(p=>p+1)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,padding:"4px 10px",fontSize:12}}>→</button>
{weekOffset!==0&&<button onClick={()=>setWeekOffset(0)} style={{...sb,background:t.sf,color:t.bl,border:`1px solid ${t.bd}`,padding:"4px 8px",fontSize:10}}>Today</button>}
</div>}

{/* Range label (non-week) */}
{viewMode!=='week'&&<div style={{fontSize:12,color:t.tm,marginBottom:8,fontWeight:600}}>{rangeLabel}</div>}

{/* Vendor filter */}
<div style={{display:"flex",gap:6,marginBottom:0,alignItems:"center",flexWrap:'wrap'}}>
{vendorFilter.map(v=><div key={v} style={{display:'flex',alignItems:'center',gap:4,padding:'3px 8px',background:t.sf,borderRadius:6,border:`1px solid ${t.bd}`,fontSize:11}}>
<span>{v}</span>
<button onClick={()=>setVendorFilter(p=>p.filter(x=>x!==v))} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:10,padding:0}}>✕</button>
</div>)}
<div style={{minWidth:320,flex:1}}>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1,marginBottom:2}}>🔍 Filter by vendor</div>
<VendorDropdown vendors={settings?.vendors||[]} value="" onSelect={v=>{const name=typeof v==='string'?v:v;setVendorFilter(p=>[...p,name]);}} placeholder={vendorFilter.length===0?'Type a vendor to filter...':'+ Add vendor'} excludeNames={vendorFilter} showAddNew={false} t={t} si={si}/>
</div>
{vendorFilter.length>0&&<button onClick={()=>setVendorFilter([])} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:10,fontFamily:"'DM Sans',sans-serif",padding:0}}>Clear all</button>}
<div style={{marginLeft:"auto",fontSize:12,color:t.tm,flexShrink:0}}>{shown.length} swap{shown.length!==1?'s':''}</div>
</div>
</div>

{shown.length===0?<div style={{textAlign:'center',padding:40,color:t.td,fontSize:12}}>No swaps in this range.</div>:
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:'hidden'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>
{['Vendor','Invoice #','Date','Type','Original','Replaced','Delta','Swapped On'].map(h=><th key={h} style={h==='Type'?{...sh,textAlign:'center'}:sh}>{h}</th>)}
</tr></thead>
<tbody>{shown.map(p=>{
const oldT=Number(p.old.grand_total||0);
const newT=p.new?Number(p.new.grand_total||0):0;
const diff=newT-oldT;
return<tr key={p.old.id} style={{borderBottom:`1px solid ${t.bd}`,cursor:p.new&&setDuplicateData?'pointer':'default'}} onClick={()=>{if(p.new&&setDuplicateData){setDuplicateData({mode:'review-swap',oldId:p.old.id,newId:p.new.id});setPg('duplicate-review');}}} onMouseOver={e=>{if(p.new)e.currentTarget.style.background=t.sfh;}} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<td style={{...sd,fontWeight:500,fontSize:12}}>{p.old.vendor}</td>
<td style={{...sd,fontSize:11,color:t.tm}}>{p.old.invoice_number||'—'}</td>
<td style={{...sd,fontSize:11,color:t.tm}}>{fmtDate(p.old.invoice_date)}</td>
<td style={{...sd,fontSize:10,whiteSpace:'nowrap',textAlign:'center'}}>{(()=>{const odt=(p.old.doc_type||'invoice').replace('_',' ');const ndt=(p.new?.doc_type||'invoice').replace('_',' ');const oc=odt==='invoice'?t.bl:t.yl;const nc=ndt==='invoice'?t.bl:t.yl;const ob=odt==='invoice'?'rgba(59,130,246,0.1)':'rgba(212,168,36,0.15)';const nb=ndt==='invoice'?'rgba(59,130,246,0.1)':'rgba(212,168,36,0.15)';return<><span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:ob,color:oc,fontWeight:600,textTransform:'uppercase',letterSpacing:0.3}}>{odt}</span><span style={{color:t.td,margin:'0 3px',fontSize:9}}>→</span><span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:nb,color:nc,fontWeight:600,textTransform:'uppercase',letterSpacing:0.3}}>{ndt}</span></>;})()}</td>
<td style={{...sd,fontFamily:mn,fontSize:11}}>
<span onClick={e=>{e.stopPropagation();if(setInvoiceModalId)setInvoiceModalId(p.old.id);}} style={{cursor:setInvoiceModalId?'pointer':'default',color:setInvoiceModalId?t.bl:t.tx}}>${fmt$0(oldT)}</span>
</td>
<td style={{...sd,fontFamily:mn,fontSize:11,fontWeight:600}}>
{p.new?<span onClick={e=>{e.stopPropagation();if(setInvoiceModalId)setInvoiceModalId(p.new.id);}} style={{cursor:setInvoiceModalId?'pointer':'default',color:setInvoiceModalId?t.bl:t.tx}}>${fmt$0(newT)}</span>:'—'}
</td>
<td style={{...sd,fontFamily:mn,fontSize:11,fontWeight:600,color:diff>0?t.gn:diff<0?t.ac:t.td}}>{diff>0?'+':diff<0?'−':''}{diff!==0?'$'+fmt$0(Math.abs(diff)):'—'}</td>
<td style={{...sd,fontSize:11,color:t.td}}>{p.new?fmtDate(p.new.created_at?.split('T')[0]):'—'}</td>
</tr>;})}
</tbody></table>
</div>}
</>}
{error&&<div style={{fontSize:11,color:t.ac,marginTop:10}}>{error}</div>}
</div>);
}
